import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Container } from '@material-ui/core';
import { get } from 'lodash';
import Form from '../../../components/RxForm/Form';
import LoadAvailableLocations from '../../../components/LoadAvailableLocations';
import RxFormsApiService from '../../../services/api/rxForms';
import LoadingButton from '../../../components/LoadingButton';
import { CONTROL_EXISTING } from '../../Account/components/RxFormCustomization/FormBuilder/helpers';
import { RETAINER_ATTRIBUTES } from '../../../components/RxForm/Retainer/constants';
import createRetainerNativeFields from '../../../components/RxForm/Retainer/nativeFields';
import { createRetainerValidationSchema } from '../../../components/RxForm/Retainer/validation';
import withOrganizationPricing from '../../../hooks/withOrganizationPricing';

const UpdateRetainerRxForm = ({
  rxForm,
  preference,
  afterSave,
  onSaveFailure,
  onCancel,
  modificationRules
}) => {
  const [saving, setSaving] = useState(false);
  const formBuilderForm = get(rxForm, 'form_builder_data.form', null);
  const formStructure = get(rxForm, 'form_builder_data.layout', []);
  const newControls = Object.values(get(formBuilderForm, 'controls', {})).filter(
    control => control.system_type !== CONTROL_EXISTING
  );
  const only = Object.keys(modificationRules);

  const data = {
    id: rxForm.id,
    [RETAINER_ATTRIBUTES.FIRST_NAME]: rxForm.first_name,
    [RETAINER_ATTRIBUTES.LAST_NAME]: rxForm.last_name,
    [RETAINER_ATTRIBUTES.CASE_NUMBER_TEXT]: rxForm.case_number_text,
    [RETAINER_ATTRIBUTES.DOCTOR]: rxForm.user_id,
    [RETAINER_ATTRIBUTES.ARCH_TYPE]: String(rxForm.jaws_id),
    [RETAINER_ATTRIBUTES.RETAINER_CUT_TYPE]: rxForm.retainer_cut_type ?? '',
    [RETAINER_ATTRIBUTES.SHIPPING_LOCATION]: rxForm.shipping_location_id,
    [RETAINER_ATTRIBUTES.SHIPPING_METHOD]: rxForm.shipping_method,
    [RETAINER_ATTRIBUTES.NOTES]: rxForm.special_instructions,
    form_builder_layout_id: rxForm.form_builder_layout_id
  };

  const save = values => {
    const api = new RxFormsApiService();
    setSaving(true);
    api
      .patch(rxForm.id, values)
      .then(() => {
        setSaving(false);
        afterSave();
      })
      .catch(error => {
        setSaving(false);
        onSaveFailure(error);
      });
  };

  const children = pricing => (
    <Container maxWidth="md">
      <LoadAvailableLocations organization={rxForm.organization}>
        {availableLocations => (
          <Form
            organization={rxForm.organization}
            availableLocations={availableLocations}
            title="Modify Retainer RxForm"
            description={`Patient: ${rxForm.last_name}, ${rxForm.first_name} - ${rxForm.organization.name}`}
            preference={preference}
            onSubmit={save}
            rxForm={data}
            isLoading={false}
            only={only}
            formStructure={formStructure}
            newControls={newControls}
            nativeFields={createRetainerNativeFields}
            createValidationSchema={createRetainerValidationSchema}
            pricing={pricing}
            actions={({ dirty, isValid, submitForm, status }) => (
              <>
                <Button disabled={saving} color="secondary" variant="contained" onClick={onCancel}>
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={saving}
                  disabled={!dirty || !isValid || !!status}
                  onClick={submitForm}
                >
                  Save
                </LoadingButton>
              </>
            )}
          />
        )}
      </LoadAvailableLocations>
    </Container>
  );

  return withOrganizationPricing({ organizationId: rxForm.organization.id, children });
};

UpdateRetainerRxForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  rxForm: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  preference: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  modificationRules: PropTypes.object.isRequired,
  afterSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSaveFailure: PropTypes.func.isRequired
};

export default UpdateRetainerRxForm;
